import React from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin, Linkedin, Instagram, Facebook } from "lucide-react";
import { useT, useLanguage } from "@/i18n/LanguageProvider";
import Chevron from "./Chevron";

const LOGO_EN = "/assets/brand/logo_eng.png";
const LOGO_AR = "/assets/brand/logo_Arabic.png";

// X logo as inline SVG (lucide ships an `X` close-icon, not the brand mark)
const XIcon = ({ size = 15 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z" />
  </svg>
);

const SOCIALS = [
  { id: "linkedin",  Icon: Linkedin,  label: "LinkedIn",  href: "https://www.linkedin.com/company/delfi-express/?viewAsMember=true" },
  { id: "instagram", Icon: Instagram, label: "Instagram", href: "https://www.instagram.com/delfiexpressoman/" },
  { id: "facebook",  Icon: Facebook,  label: "Facebook",  href: "#" },
  { id: "x",         Icon: XIcon,     label: "X",         href: "https://x.com/delfiexpress" },
];

export default function Footer() {
  const t = useT();
  const { lang } = useLanguage();
  const year = new Date().getFullYear();
  const copyright = (t.footer.copyright || "").replace("{year}", year);
  const logoSrc = lang === "ar" ? LOGO_AR : LOGO_EN;

  return (
    <footer className="bg-delfi-dark text-white relative overflow-hidden" data-testid="site-footer">
      <div className="chevron-bg right-[-8%] bottom-[-10%] opacity-[0.06]" aria-hidden="true">›</div>

      <div className="delfi-container relative py-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
        <div>
          <img
            src={logoSrc}
            alt="Twasal Awasal"
            className="h-12 w-auto object-contain mb-5"
            style={{ maxWidth: 170 }}
          />
          <p className="text-white/70 text-sm leading-relaxed max-w-xs">{t.footer.tagline}</p>

          {/* Social icons */}
          <div className="mt-6" data-testid="footer-socials">
            <p className="text-[10px] uppercase tracking-[0.22em] text-white/45 font-semibold mb-3">
              {t.footer.socialLabel}
            </p>
            <div className="flex items-center gap-2.5">
              {SOCIALS.map(({ id, Icon, label, href }) => (
                <a
                  key={id}
                  href={href}
                  target={href !== "#" ? "_blank" : undefined}
                  rel={href !== "#" ? "noopener noreferrer" : undefined}
                  aria-label={label}
                  data-testid={`footer-social-${id}`}
                  className="w-9 h-9 rounded-full bg-white/[0.06] border border-white/15 flex items-center justify-center text-white/80 hover:text-white hover:bg-delfi-red/90 hover:border-delfi-red/80 transition-all duration-300"
                >
                  <Icon size={15} strokeWidth={1.8} />
                </a>
              ))}
            </div>
          </div>

          {/* App store badges */}
          <div className="mt-6" data-testid="footer-app-badges">
            <p className="text-[10px] uppercase tracking-[0.22em] text-white/45 font-semibold mb-3">
              {t.appDownload?.eyebrow || "Mobile App"}
            </p>
            <div className="flex flex-wrap items-center gap-2.5">
              <a
                href="#"
                aria-label="Download on the App Store"
                data-testid="footer-app-store"
                className="flex items-center gap-2 px-3 py-2 rounded-xl bg-black text-white border border-white/10 hover:border-delfi-red/60 transition-all duration-300"
              >
                <svg viewBox="0 0 384 512" className="w-5 h-5 fill-current" aria-hidden="true">
                  <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zM236.5 105.3c20.1-24.3 31-50.9 28.4-79.3-25.6 1.6-56.8 17.5-74.6 38.8-15.6 18.4-29.8 47.2-26.5 73.7 28.6 2.4 56.5-13.4 72.7-33.2z" />
                </svg>
                <span className="flex flex-col leading-tight">
                  <span className="text-[8px] tracking-wide opacity-80 uppercase">
                    {t.appDownload?.appleSubtitle || "Download on the"}
                  </span>
                  <span className="text-xs font-semibold tracking-tight">
                    {t.appDownload?.appleTitle || "App Store"}
                  </span>
                </span>
              </a>
              <a
                href="#"
                aria-label="Get it on Google Play"
                data-testid="footer-google-play"
                className="flex items-center gap-2 px-3 py-2 rounded-xl bg-black text-white border border-white/10 hover:border-delfi-red/60 transition-all duration-300"
              >
                <svg viewBox="0 0 512 512" className="w-5 h-5" aria-hidden="true">
                  <path d="M325.3 234.3 104.6 13l280.8 161.2-60.1 60.1z" fill="#EA4335" />
                  <path d="M104.6 499 325.3 277.7l-60.1-60.1L104.6 13z" fill="#FBBC04" />
                  <path d="M104.6 13v486l116.2-243z" fill="#34A853" />
                  <path d="m385.4 337-60.1-59.3L265.2 217.6 104.6 499z" fill="#4285F4" />
                </svg>
                <span className="flex flex-col leading-tight">
                  <span className="text-[8px] tracking-wide opacity-80 uppercase">
                    {t.appDownload?.googleSubtitle || "Get it on"}
                  </span>
                  <span className="text-xs font-semibold tracking-tight">
                    {t.appDownload?.googleTitle || "Google Play"}
                  </span>
                </span>
              </a>
            </div>
          </div>
        </div>

        <div>
          <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Chevron size={14} /> {t.footer.platform}
          </h4>
          <ul className="space-y-2.5 text-white/70 text-sm">
            <li><Link to="/services" className="hover:text-white" data-testid="footer-link-features">{t.footer.links.features}</Link></li>
            <li><Link to="/track" className="hover:text-white" data-testid="footer-link-track">{t.footer.links.track}</Link></li>
            <li><Link to="/business-solutions" className="hover:text-white" data-testid="footer-link-solutions">{t.footer.links.solutions}</Link></li>
            <li><Link to="/pricing" className="hover:text-white" data-testid="footer-link-pricing">{t.footer.links.pricing}</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Chevron size={14} /> {t.footer.company}
          </h4>
          <ul className="space-y-2.5 text-white/70 text-sm">
            <li><Link to="/about" className="hover:text-white" data-testid="footer-link-about">{t.footer.links.about}</Link></li>
            <li><Link to="/contact" className="hover:text-white" data-testid="footer-link-partner">{t.footer.links.partner}</Link></li>
            <li><Link to="/login" className="hover:text-white" data-testid="footer-link-login">{t.footer.links.login}</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Chevron size={14} /> {t.footer.contact}
          </h4>
          <ul className="space-y-3 text-white/70 text-sm">
            <li className="flex items-start gap-3">
              <MapPin size={16} className="text-delfi-red mt-0.5 shrink-0" />
              <span>{t.footer.location}</span>
            </li>
            <li className="flex items-start gap-3">
              <Mail size={16} className="text-delfi-red mt-0.5 shrink-0" />
              <a href="mailto:hello@twasalawasal.com" className="hover:text-white">hello@twasalawasal.com</a>
            </li>
            <li className="flex items-start gap-3">
              <Mail size={16} className="text-delfi-red mt-0.5 shrink-0" />
              <a href="mailto:business@twasalawasal.com" className="hover:text-white">business@twasalawasal.com</a>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="delfi-container py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-white/50 text-xs">
          <p data-testid="footer-copyright">{copyright}</p>
          <p className="flex items-center gap-2">
            <Chevron size={12} /> {t.footer.motto}
          </p>
        </div>
      </div>
    </footer>
  );
}
